"use client";

import React from "react";
import { ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const upgradePerks = [{
  title: "100% Purchase Credit",
  description: "Every dollar spent on the Legacy Expedition Suite is credited directly against your design retainer."
}, {
  title: "Preserved Planning Context",
  description: "Our travel architects inherit your completed surveys and matrix data for immediate execution."
}, {
  title: "Direct Duo Access",
  description: "Transition straight into private itinerary architecture led by Scott and Michael."
}];
export default function Upgrade() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.65,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="inception-upgrade-offer" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="relative overflow-hidden rounded-md bg-primary p-8 sm:p-12 lg:p-16 text-primary-foreground shadow-xl border border-border/30">
          {/* Subtle architectural orbit graphic in background */}
          <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 size-96 rounded-full border border-primary-foreground/10" />
          <div aria-hidden="true" className="pointer-events-none absolute -right-12 -top-12 size-72 rounded-full border border-primary-foreground/10" />

          <div className="relative z-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12 items-center">
            {/* Left Column: Narrative Copy */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/10 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-primary-foreground">
                <Sparkles className="size-3.5 text-primary-foreground" />
                <span>The Inception Upgrade Promise</span>
              </div>

              <div className="space-y-3">
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-primary-foreground leading-tight">
                  Upgrade to bespoke curation anytime.
                </h2>
                <p className="font-sans text-base sm:text-lg text-primary-foreground/90 max-w-2xl leading-relaxed">
                  Start with the self-guided framework today. If your multi-generational milestone calls for full-service mediation and on-the-ground concierge execution, your entire $69 suite purchase is credited back in full against your travel design retainer.
                </p>
              </div>

              <div className="pt-2">
                <Button asChild variant="outline" size="lg" className="rounded-sm border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground hover:text-primary transition-[color,background-color,border-color] duration-300 font-sans tracking-wide uppercase text-xs sm:text-sm font-semibold h-12 px-6">
                  <Link to="/AboutDiscoveryConsultationPage">
                    <span>Schedule a discovery consultation</span>
                    <ArrowRight className="size-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right Column: Key Commitments */}
            <div className="lg:col-span-5 bg-primary-foreground/5 rounded-md p-6 sm:p-8 border border-primary-foreground/15 backdrop-blur-xs space-y-6">
              <div className="flex items-center gap-3 pb-3 border-b border-primary-foreground/15">
                <ShieldCheck className="size-5 text-primary-foreground shrink-0" />
                <span className="font-sans text-xs uppercase tracking-widest font-semibold text-primary-foreground">
                  Zero-Risk Transition
                </span>
              </div>

              <div className="space-y-5">
                {upgradePerks.map((perk, i) => <div key={perk.title} data-index={i} className="space-y-1">
                    <h3 className="font-serif text-lg font-medium text-primary-foreground">
                      {perk.title}
                    </h3>
                    <p className="font-sans text-xs sm:text-sm text-primary-foreground/80 leading-relaxed">
                      {perk.description}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}